/**
 * Mirror of `WorkItemsController.ComposeDecisionTitle`.
 *
 * The capture dialog previews the title that will be saved, so it has to agree
 * with the server in the cases the server special-cases — otherwise the preview
 * promises one thing and the decision log shows another, which is worse than
 * showing no preview at all.
 *
 * Keep the two in lockstep. `composeDecisionTitle.test.ts` pins the behaviour
 * the API tests assert server-side.
 */
const MAX_LENGTH = 500;            // Decision.Title is nvarchar(500)
const SEPARATOR = ' — ';
const MIN_USEFUL_QUESTION_CHARS = 12;

function truncate(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  let cut = maxLength - 1;         // room for the ellipsis
  // Don't split a surrogate pair — a lone surrogate renders as a replacement
  // character. JS strings are UTF-16, same as the server's.
  const code = value.charCodeAt(cut);
  if (cut > 0 && code >= 0xdc00 && code <= 0xdfff) cut--;
  return value.slice(0, cut).replace(/\s+$/, '') + '…';
}

function isWordChar(ch: string): boolean {
  return /[\p{L}\p{N}]/u.test(ch);
}

export function composeDecisionTitle(workItemTitle: string, outcome: string): string {
  const question = (workItemTitle ?? '').trim();
  const answer = (outcome ?? '').trim();

  if (question.length === 0) return truncate(answer, MAX_LENGTH);
  if (answer.length === 0) return truncate(question, MAX_LENGTH);

  // Only a repeat if the question ends on a word boundary within the answer —
  // "Approve" must not be swallowed by "Approved".
  if (answer.toLowerCase().startsWith(question.toLowerCase())
      && (answer.length === question.length || !isWordChar(answer[question.length]))) {
    return truncate(answer, MAX_LENGTH);
  }

  const room = MAX_LENGTH - SEPARATOR.length - answer.length;
  if (room < MIN_USEFUL_QUESTION_CHARS) return truncate(answer, MAX_LENGTH);

  return question.length <= room
    ? question + SEPARATOR + answer
    : truncate(question, room) + SEPARATOR + answer;
}
